import cloneDeep from 'lodash/cloneDeep.js';

import useDueDateExtensionPrompt from '@/composables/useDueDateExtensionPrompt.js';
import { handleResponseError } from '@/Helpers.js';
import ActivityHelper from '@/plugins/autosaver/activities/activity-helper.js';
import axios from '@/plugins/axios.js';
import { isAxiosCancel } from '@/utilities/CancelSource.js';

const namespace = name => {
    return name.substring(0, name.indexOf('/'));
};

const helper = new ActivityHelper();

const dispatchActivitiesSave = (store, mutation, activity, onResolved) =>
    store
        .dispatch('AutoSave/activities', { mutation: mutation.type, activity })
        .then(result => {
            if (typeof onResolved === 'function') onResolved(result);
            return result;
        })
        .catch(err => {
            if (!isAxiosCancel(err)) {
                throw err;
            }
        });

export default {
    'ActivityTemplates/EDIT': {
        fields: ['title', 'description', 'activity_type', 'media', 'prerequisite_uuid'],
        callback: (store, mutation) => {
            const activity = store.getters['ActivityTemplates/get'](mutation.payload.uuid);
            if (!activity) return;

            // File submission specific validation
            if (activity.activity_type === 'file-submission') {
                if (activity.activity_content.min_files < 0 || activity.activity_content.max_files < 0) return;
                if (activity.activity_content.min_files > activity.activity_content.max_files) return;
                if (activity.activity_settings.max_attempts_allowed < 0) return;

                if (
                    activity.activity_content.is_turnitin_submission &&
                    !activity.activity_content.lti_resource_content_id
                )
                    return;
            }

            const whiteListContent = ['survey', 'quiz', 'presentation', 'flashcard', 'slide-deck'].some(
                check => check === activity.activity_type
            );

            const quiz = ['survey', 'quiz'].some(check => check === activity.activity_type);

            // Check required fields from ActivityTemplateRequest
            if (!activity.activity_type) return;
            if (!activity.title || activity.title.trim() === '') return;
            if (activity.title.length > 191) return;
            if (typeof activity.order === 'undefined' || activity.order === null) return;

            // Check activity content requirements
            if (!whiteListContent && !activity.activity_content.content) return;

            // Allow initial template creation even if settings template isn't built yet.
            if (
                activity.id &&
                (!activity.activity_settings_template || typeof activity.activity_settings_template !== 'object')
            )
                return;

            // For new activity templates, ensure they have a course template ID
            if (!activity.id && !activity.course_template_id) return;

            if (quiz && !activity.activity_content.quiz_id) return;

            if (activity.activity_type === 'video' && !activity.activity_content.video_id) return;

            if (activity.activity_type === 'flashcard' && !activity.activity_content.flashcard_set_id) return;

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_settings_template?.is_remediation &&
                !activity.activity_settings_template?.points_possible
            )
                return;

            store.dispatch('AutoSave/setSaving', mutation);

            dispatchActivitiesSave(store, mutation, activity, () => {
                store.dispatch('AutoSave/setSaved', mutation);
            });
        },
    },

    'ActivityTemplates/DELETE_MEDIA': {
        fields: ['media'],
        callback: async (store, mutation) => {
            const { payload } = mutation;
            const activityTemplate = store.getters['ActivityTemplates/get'](mutation.payload.uuid);

            store.dispatch('AutoSave/setSaving', mutation);

            try {
                await axios.delete(`/api/activity-templates/${activityTemplate.id}/media`, {
                    data: { delete_media: [payload.media.id] },
                });

                store.dispatch('AutoSave/setSaved', mutation);
            } catch (error) {
                store.commit('ActivityTemplates/EDIT', {
                    uuid: payload.uuid,
                    field: 'media',
                    value: [...(store.getters['ActivityTemplates/get'](payload.uuid)?.media || []), payload.media],
                });

                handleResponseError(error);
                await store.dispatch('AutoSave/setError', { indicator: mutation.type, error });
            }
        },
    },

    'Activities/EDIT': {
        fields: ['title', 'description', 'activity_type', 'media', 'prerequisite_uuid'],
        callback: (store, mutation) => {
            const activity = store.getters['Activities/get'](mutation.payload.uuid);
            if (!activity) return;

            const whiteListContent = ['survey', 'quiz', 'presentation', 'flashcard', 'slide-deck'].some(
                check => check === activity.activity_type
            );

            const quiz = ['survey', 'quiz'].some(check => check === activity.activity_type);

            if (!activity || !activity.activity_type || !activity.title || activity.title.length > 191) return;

            if (activity.activity_type === 'video' && !activity.activity_content.video_id) return;

            if (activity.activity_type === 'slide-deck' && !activity.activity_content.slide_deck_id) return;

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_content.is_turnitin_submission &&
                !activity.activity_content.lti_resource_link_id
            )
                return;

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_settings.is_remediation &&
                !activity.activity_settings.points_possible
            )
                return;

            if (quiz && !activity.activity_content.quiz_id) return;

            if (activity.activity_type === 'flashcard' && !activity.activity_content.flashcard_set_id) return;

            if (
                !activity ||
                !activity.activity_type ||
                (!activity.activity_content.content && !whiteListContent) ||
                !activity.title
            )
                return;

            store.dispatch('AutoSave/setSaving', mutation);

            dispatchActivitiesSave(store, mutation, activity, () => {
                // New activity persisted: clear "newly created" styling by turning off editing
                store.dispatch('Activities/updateItem', {
                    uuid: activity.uuid,
                    field: 'displayStatus',
                    value: { editing: false },
                });
                store.dispatch('AutoSave/setSaved', mutation);
            });
        },
    },

    'Activities/DELETE_MEDIA': {
        fields: ['media'],
        callback: async (store, mutation) => {
            const { payload } = mutation;
            const activity = store.getters['Activities/get'](mutation.payload.uuid);

            store.dispatch('AutoSave/setSaving', mutation);

            try {
                await axios.delete(`/api/activities/${activity.id}/media`, {
                    data: { delete_media: [payload.media.id] },
                });

                store.dispatch('AutoSave/setSaved', mutation);
            } catch (error) {
                store.commit('Activities/EDIT', {
                    uuid: payload.uuid,
                    field: 'media',
                    value: [...(store.getters['Activities/get'](payload.uuid)?.media || []), payload.media],
                });

                handleResponseError(error);
                await store.dispatch('AutoSave/setError', { indicator: mutation.type, error });
            }
        },
    },

    'ActivityTemplates/CONTENT': {
        fields: [
            'content',
            'points_possible',
            'acknowledgment_message',
            'max_files',
            'min_files',
            'min_threads',
            'max_threads',
            'thread_min_words',
            'min_replies',
            'reply_min_words',
            'video_id',
            'slide_deck_id',
            'elearning_package_id',
            'lti_resource_link_id',
            'lti_resource_content_id',
            'flashcard_set_id',
            'can_post_threads_after_grade',
            'restrict_until_week_start',
            'is_turnitin_submission',
            'selection_mode',
            'ebook_provider_id',
            'podcast_length',
            'generation_context',
            'podcast_config',
            'script',
        ],
        callback: (store, mutation) => {
            const activity = store.getters['ActivityTemplates/get'](mutation.payload.uuid);

            if (activity.activity_content.max_files < 0) {
                return;
            }

            if (activity.activity_content.min_files < 0) {
                return;
            }

            if (mutation.payload.field === 'video_id' && !mutation.payload.value) return;

            if (mutation.payload.field === 'slide_deck_id' && !mutation.payload.value) return;

            if (mutation.payload.field === 'lti_resource_link_id' && mutation.payload.value === undefined) return;

            if (mutation.payload.field === 'flashcard_set_id' && !mutation.payload.value) return;

            // Enhanced validation for required fields
            if (!activity) return;
            if (!activity.activity_type) return;
            if (!activity.title || activity.title.trim() === '') return;
            if (activity.title.length > 191) return;
            if (typeof activity.order === 'undefined' || activity.order === null) return;

            // Content validation - must have meaningful content (except for whitelisted types)
            const contentWhiteList = ['survey', 'quiz', 'presentation', 'flashcard', 'podcast', 'slide-deck'].some(
                check => check === activity.activity_type
            );
            if (
                !contentWhiteList &&
                (!activity.activity_content?.content || activity.activity_content.content.trim() === '')
            )
                return;

            if (activity.activity_type === 'video' && !activity.activity_content.video_id) return;

            if (activity.activity_type === 'lti-resource-link' && !activity.activity_content.lti_resource_link_id) {
                return;
            }

            if (
                activity.activity_type === 'lti-tool' &&
                (!activity.activity_content.lti_resource_link_id || !activity.activity_content.lti_resource_content_id)
            ) {
                return;
            }

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_content.is_turnitin_submission &&
                activity.activity_content.lti_resource_content_id === undefined
            ) {
                return;
            }

            if (activity.activity_type === 'flashcard' && !activity.activity_content.flashcard_set_id) return;

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_settings_template.is_remediation &&
                !activity.activity_settings_template.points_possible
            )
                return;

            store.dispatch('AutoSave/setSaving', mutation);

            dispatchActivitiesSave(store, mutation, activity, () => {
                store.dispatch('AutoSave/setSaved', mutation);
            });
        },
    },

    'Activities/CONTENT': {
        fields: [
            'content',
            'points_possible',
            'acknowledgment_message',
            'max_files',
            'min_files',
            'min_threads',
            'max_threads',
            'thread_min_words',
            'min_replies',
            'reply_min_words',
            'video_id',
            'slide_deck_id',
            'elearning_package_id',
            'lti_resource_link_id',
            'lti_resource_content_id',
            'flashcard_set_id',
            'can_post_threads_after_grade',
            'is_turnitin_submission',
            'selection_mode',
            'ebook_provider_id',
            'restrict_until_week_start',
            'podcast_length',
            'generation_context',
            'podcast_config',
            'script',
        ],
        callback: (store, mutation) => {
            const activity = store.getters['Activities/get'](mutation.payload.uuid);
            const contentWhiteList = ['survey', 'quiz', 'presentation', 'podcast', 'flashcard', 'slide-deck'].some(
                check => check === activity.activity_type
            );

            if (
                activity.activity_type === 'file-submission' &&
                (activity.activity_content.min_files < 0 || activity.activity_content.max_files < 0)
            )
                return;

            if (mutation.payload.field === 'video_id' && !mutation.payload.value) return;

            if (mutation.payload.field === 'lti_resource_link_id' && !mutation.payload.value) return;

            if (mutation.payload.field === 'slide_deck_id' && !mutation.payload.value) return;

            if (mutation.payload.field === 'flashcard_set_id' && !mutation.payload.value) return;

            if (
                !activity ||
                !activity.activity_type ||
                (!contentWhiteList &&
                    (!activity.activity_content?.content || activity.activity_content.content.trim() === '')) ||
                !activity.title ||
                activity.title.length > 191
            )
                return;

            if (activity.activity_type === 'video' && !activity.activity_content.video_id) return;

            if (activity.activity_type === 'slide-deck' && !activity.activity_content.slide_deck_id) return;

            if (activity.activity_type === 'lti-resource-link' && !activity.activity_content.lti_resource_link_id) {
                return;
            }

            if (
                activity.activity_type === 'lti-tool' &&
                (!activity.activity_content.lti_resource_link_id || !activity.activity_content.lti_resource_content_id)
            ) {
                return;
            }

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_content.is_turnitin_submission &&
                !activity.activity_content.lti_resource_link_id
            ) {
                return;
            }

            if (activity.activity_type === 'flashcard' && !activity.activity_content.flashcard_set_id) return;

            if (
                activity.activity_type === 'file-submission' &&
                activity.activity_settings.is_remediation &&
                !activity.activity_settings.points_possible
            )
                return;

            store.dispatch('AutoSave/setSaving', mutation);

            dispatchActivitiesSave(store, mutation, activity, () => {
                store.dispatch('AutoSave/setSaved', mutation);
            });
        },
    },

    'Activities/SETTINGS': {
        fields: [
            'allow_submissions_after_due_date',
            'max_attempts_allowed',
            'points_possible',
            'auto_grade',
            'visible_at',
            'due_at',
            'is_downloadable',
            'is_extra_credit',
            'course_authorized_role_id',
            'is_for_attendance',
            'minimum_passing_score',
            'acknowledgment',
            'allow_student_attachments',
        ],
        callback: (store, mutation) => {
            const activity = cloneDeep(store.getters[`${namespace(mutation.type)}/get`](mutation.payload.uuid));

            if (activity.activity_type === 'file-submission' && activity.activity_settings.max_attempts_allowed < 0)
                return;

            if (!activity.id) return;

            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/activitySettings', {
                    mutation: mutation.type,
                    activity,
                })
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);

                    if (mutation.payload.field === 'due_at' && mutation.type === 'Activities/SETTINGS') {
                        useDueDateExtensionPrompt().checkAfterSave(activity);
                    }
                })
                .catch(err => {
                    if (!isAxiosCancel(err)) {
                        throw err;
                    }
                });
        },
    },

    'ActivityTemplates/SETTINGS': {
        fields: [
            'allow_submissions_after_due_date',
            'max_attempts_allowed',
            'remove_visible_at',
            'points_possible',
            'auto_grade',
            'week_visible',
            'day_visible',
            'time_visible',
            'week_due',
            'day_due',
            'time_due',
            'is_extra_credit',
            'course_authorized_role_id',
            'is_for_attendance',
            'minimum_passing_score',
            'acknowledgment',
            'allow_student_attachments',
        ],
        callback: (store, mutation) => {
            const activity = cloneDeep(store.getters[`${namespace(mutation.type)}/get`](mutation.payload.uuid));
            const settings = 'activity_settings_template';

            const hasValidSettings = helper.validateActivitySettings({
                settings: activity[settings],
                mutation,
            });

            if (activity.activity_type === 'file-submission' && activity[settings].max_attempts_allowed < 0) return;

            if (!hasValidSettings) return;

            if (!activity.id) return;

            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/activitySettings', {
                    mutation: mutation.type,
                    activity,
                })
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                })
                .catch(err => {
                    if (!isAxiosCancel(err)) {
                        throw err;
                    }
                });
        },
    },
};
